import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import {
  fetchGetCurrencies,
  fetchGetFiatUserAccess,
} from '@shared-runtime/modules/fiat';
import type { CurrencyStatus } from '@shared-runtime/modules/fiat/get-currencies/schema';
import { useAuth } from '@shared-web/modules/auth/hooks/useAuth';
import type { DepositMethod } from '@shared-web/modules/deposit/types';
import type { WithdrawMethod } from '@shared-web/modules/withdraw/types';

import {
  API_BANK_NETWORK,
  FUNDS_DIRECTION,
  METHOD_NETWORK_MAP,
} from '../types/constants';

export interface CurrencyWithStatus {
  currency: string;
  status?: string;
  isActivated?: boolean;
  tncAccepted?: boolean;
  hasAccess?: boolean;
  currencyInfo?: CurrencyStatus;
}

export function useFiatAccess() {
  const { isLogin } = useAuth();

  const {
    data: userAccess,
    isLoading,
    refetch,
    isFetched,
  } = useQuery({
    queryKey: ['fiat-user-access'],
    queryFn: () => fetchGetFiatUserAccess({}),
    enabled: isLogin,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const {
    data: currenciesData,
    isLoading: currenciesLoading,
    refetch: refetchCurrencies,
  } = useQuery({
    queryKey: ['fiat-currencies'],
    queryFn: () => fetchGetCurrencies({}),
    enabled: isLogin,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const userAccessItems = useMemo(
    () => userAccess?.items || [],
    [userAccess?.items],
  );

  const hasSwiftAccess = useMemo(
    () =>
      userAccessItems.some(
        (item) => item.serviceName === API_BANK_NETWORK.SWIFT,
      ),
    [userAccessItems],
  );

  const hasFedwireAccess = useMemo(
    () =>
      userAccessItems.some(
        (item) => item.serviceName === API_BANK_NETWORK.FEDWIRE,
      ),
    [userAccessItems],
  );

  const hasCubixAccess = useMemo(
    () =>
      userAccessItems.some(
        (item) => item.serviceName === API_BANK_NETWORK.CUBIX,
      ),
    [userAccessItems],
  );

  const hasACHAccess = useMemo(
    () =>
      userAccessItems.some((item) => item.serviceName === API_BANK_NETWORK.ACH),
    [userAccessItems],
  );

  const hasUSDAccess = useMemo(
    () => userAccess?.hasUSDAccess || !!currenciesData?.data.USD,
    [userAccess, currenciesData],
  );

  const hasEURAccess = useMemo(
    () => userAccess?.hasEURAccess || !!currenciesData?.data.EUR,
    [userAccess, currenciesData],
  );

  const hasGBPAccess = useMemo(
    () => userAccess?.hasGBPAccess || !!currenciesData?.data.GBP,
    [userAccess, currenciesData],
  );

  const hasAEDAccess = useMemo(
    () => userAccess?.hasAEDAccess || !!currenciesData?.data.AED,
    [userAccess, currenciesData],
  );

  const allCurrencies = useMemo(() => {
    const currencies: CurrencyWithStatus[] = [];

    const currencyMapping: Array<{
      currency: 'USD' | 'EUR' | 'GBP' | 'AED';
      hasAccess: boolean;
    }> = [
      { currency: 'USD', hasAccess: hasUSDAccess },
      { currency: 'EUR', hasAccess: hasEURAccess },
      { currency: 'GBP', hasAccess: hasGBPAccess },
      { currency: 'AED', hasAccess: hasAEDAccess },
    ];

    currencyMapping.forEach(({ currency, hasAccess }) => {
      const currencyInfo = currenciesData?.data?.[currency];

      if (hasAccess) {
        currencies.push({
          currency,
          status: currencyInfo?.status,
          isActivated: !!currencyInfo?.isActivated,
          tncAccepted: !!currencyInfo?.tncAccepted,
          hasAccess: true,
          currencyInfo,
        });
      } else {
        currencies.push({
          currency,
          isActivated: false,
        });
      }
    });

    return currencies;
  }, [hasUSDAccess, hasEURAccess, hasGBPAccess, hasAEDAccess, currenciesData]);

  const supportedCurrencies = useMemo(() => {
    return allCurrencies.filter((item) => item.hasAccess);
  }, [allCurrencies]);

  const getPaymentNetworksForCurrency = useMemo(() => {
    return (
      currency: string,
      type: FUNDS_DIRECTION,
      method?: DepositMethod | WithdrawMethod,
    ) => {
      const isDeposit = type === FUNDS_DIRECTION.DEPOSIT;
      const key = isDeposit ? 'isDepositEnable' : 'isWithdrawEnable';
      let list = userAccessItems.filter(
        (item) => item.currency === currency && item[key],
      );
      const networkMap = METHOD_NETWORK_MAP[type] as Record<
        DepositMethod | WithdrawMethod,
        string[]
      >;
      if (method && networkMap[method]) {
        const networks = networkMap[method] || [];
        list = list.filter((item) => networks.includes(item.paymentNetwork));
      }
      return list;
    };
  }, [userAccessItems]);

  return {
    userAccess: userAccess || { items: [] },
    userAccessItems,
    loading: isLoading || currenciesLoading,
    userAccessFetched: isFetched,
    hasSwiftAccess,
    hasFedwireAccess,
    hasCubixAccess,
    hasACHAccess,
    hasUSDAccess,
    hasEURAccess,
    hasGBPAccess,
    hasAEDAccess,
    allCurrencies,
    supportedCurrencies,
    currenciesData,
    getPaymentNetworksForCurrency,
    getUserAccess: refetch,
    refreshCurrencies: refetchCurrencies,
  };
}
